﻿import Swiper, { Navigation, Pagination } from 'swiper';
/* eslint-disable-next-line */
import { sitecore, utils, MediaQuery } from '../../Foundation';

export class FullWidthCarousel {
  /**
   * Full Width Carousel
   * Display an carousel that covers viewport
   * @param {Carousel} element
   */
  constructor(element) {
    this._refs = {
      el: element,
      swiper: element.querySelector('.swiper'),
      slides: Array.from(element.querySelectorAll('.swiper-slide')),
      next: element.querySelector('.product-carousel__button-next'),
      prev: element.querySelector('.product-carousel__button-prev'),
      context: Array.from(element.querySelectorAll('.product-carousel__context')),
      pagination: element.querySelector('.product-carousel__pagination'),
    };

    // # Page editor
    if (sitecore.isInPageEditor) {
      return false;
    }

    // # Rendering
    if (!this._refs.slides.length) {
      // There is no items to render, abort
      return false;
    } else if (this._refs.slides.length === 1) {
      // Only one slide, convert to a standalone layout without swiper attached
      this._refs.slides[0].classList.add('is-active');
      if (this._refs.next != null) {
        this._refs.next.classList.add('hidden');
      }

      if (this._refs.prev != null) {
        this._refs.prev.classList.add('hidden');
      }

      return false;
    }

    // configure Swiper to use modules
    Swiper.use([Navigation, Pagination]);

    // # Initialize Slider
    this.slider = new Swiper(this._refs.swiper, this._sliderSettings);

    // Observer changes on active slide
    this._initObserver();

    window.addEventListener('resize', this._contentHeight.bind(this));
    window.addEventListener('load', this._contentHeight.bind(this));
  }

  _contentHeight() {
    let desktopHeight = this._refs.swiper.dataset.desktopheight;
    let mobileHeight = this._refs.swiper.dataset.mobileheight;

    if (desktopHeight) {
      this._refs.swiper.style.setProperty('--carousel-height-desktop', `${desktopHeight}px`);
    }
    if (mobileHeight) {
      this._refs.swiper.style.setProperty('--carousel-height-mobile', `${mobileHeight}px`);
    }
  }

  /**
   * Get settings for carousel
   * @returns {Object}
   */
  get _sliderSettings() {
    return {
      speed: 600,
      pagination: {
        el: this._refs.pagination,
        dynamicBullets: true,
        bulletElement: 'li',
        clickable: true,
        renderBullet: (index, className) => {
          const slide = this._refs.slides[index],
            oBullet =
              typeof slide.dataset != 'undefined' && typeof slide.dataset.bullet != 'undefined'
                ? JSON.parse(slide.dataset.bullet)
                : {};

          let innerHtml = '';

          if (Object.keys(oBullet).length) {
            if (typeof oBullet.image != 'undefined' && oBullet.image.length) {
              innerHtml +=
                '<span><img src="' +
                oBullet.image +
                '" class="product-carousel__pagination-item-image"></span>';
            } else {
              innerHtml += '<span></span>';
            }

            if (typeof oBullet.title != 'undefined' && oBullet.title.length) {
              innerHtml +=
                '<span class="product-carousel__pagination-item-title">' +
                oBullet.title +
                '</span>';
            }
          } else {
            innerHtml += '<span></span>';
          }

          return '<li class="' + className + '">' + innerHtml + '</li>';
        },
        bulletClass: 'product-carousel__pagination-item',
        bulletActiveClass: 'is-active',
        currentClass: 'is-current',
        hiddenClass: 'is-hidden',
      },
      navigation: {
        nextEl: this._refs.next,
        prevEl: this._refs.prev,
        disabledClass: 'product-carousel__button--disabled',
        hiddenClass: 'product-carousel__button--hidden',
      },
      slidesPerView: 1,
      runCallbacksOnInit: false,
      slideActiveClass: 'is-active',
    };
  }

  /**
   * Observer for changes on swiper element
   */
  _initObserver() {
    const joinString = (array) => {
      return `.swiper-slide ${array.join(', .swiper-slide ')}`;
    };
    /**
     * Only elements that are visible should be tab-able
     */
    const setTabIndex = () => {
      const activeSlide = Array.from(allSlides).filter((slide) =>
        slide.className.includes('is-active'),
      );

      const tabableElementsString = tabableElementsArray.join(',');
      const activeSlideElements = activeSlide[0].querySelectorAll(tabableElementsString);

      const allCarouselVideos = document.querySelectorAll('.background-media__video');
      Array.from(allCarouselVideos, (e) => e.pause());

      const currentVideo = activeSlide[0].querySelector('.background-media__video');

      if (activeSlide[0].classList.contains('is-active')) {
        currentVideo?.play();
      }
      // All elements
      for (const elem of allTabableElememts) {
        elem.setAttribute('tabindex', '-1');
      }
      // Visible elements
      for (const elem of activeSlideElements) {
        elem.setAttribute('tabindex', '0');
      }
    };

    // Mutation Observer on Active slide
    /* eslint-disable-next-line */
    const observerCallback = (mutationList, observer) => {
      mutationList.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // handle class change
          setTabIndex();
        }
      });
    };

    // Variables
    const tabableElementsArray = ['a', 'button', 'input', 'textarea'];
    const allTabableElememts = this._refs.el.querySelectorAll(joinString(tabableElementsArray));
    const allSlides = this._refs.el.querySelectorAll('.swiper-slide');
    const activeSlide = this._refs.el.querySelector('.swiper-slide.is-active');

    // Create new observer instance
    const observer = new MutationObserver(observerCallback);

    // Attach observer to an element
    observer.observe(activeSlide, { attributes: true });

    // Set Tab index on init
    setTabIndex();
  }
}
